<template>
  <div class="checkout-section checkout-end">
    <div ref="header">
      <slot v-if="booking.type === 'reserved'" name="confirmationHeader-Reserved"></slot>
      <slot v-else name="confirmationHeader"></slot>
    </div>

    <div class="container-fluid">
      <div class="row">
        <div class="checkout-end__success">
          <div class="checkout-end__booking">
            <div class="checkout-end__booking-details">
              <h3
                class="checkout-end__booking-title"
              >Your booking reference is: {{bookingReference}}</h3>
              <div class="checkout-end__booking-tick"></div>
              <div
                class="checkout-end__booking-description"
                v-html="confirmationType.descriptionHtml"
              ></div>
            </div>

            <div class="checkout-end__booking-next-steps">
              <h3 class="checkout-end__booking-title">{{confirmationType.nextStepsTitle}}</h3>
              <div v-html="confirmationType.nextStepsDescriptionHtml"></div>
              <a
                v-if="confirmationType.nextStepsLink"
                :href="confirmationType.nextStepsLink.url"
                class="btn"
                :class="{ 'btn_Ts': data.brandNameFormatted === Brand.Travelsphere }"
              >{{confirmationType.nextStepsLink.name}}</a>
            </div>
          </div>

          <div v-if="booking" class="checkout-end__summary"
              :class="{ 'checkout-end__summaryTs': data.brandNameFormatted === Brand.Travelsphere }">
            <h2 class="checkout-end__summary-title">{{booking.tourName}} - Summary</h2>

            <div class="checkout-end__summary-section-wrapper">
              <template v-if="booking.flights.length">
                <div
                  class="checkout-end__summary-section"
                  v-for="(flight, index) in booking.flights"
                  :key="flight.carrierCode"
                >
                  <h3 class="checkout-end__summary-section-title">Flight {{index + 1}}</h3>
                  <div class="checkout-end__info">
                    <b>Airline</b>
                    : {{flight.airline}}
                  </div>
                  <div class="checkout-end__info">
                    <b>Departure airport</b>
                    : {{flight.departureAirport}}
                  </div>
                  <div class="checkout-end__info">
                    <b>Departure date</b>
                    : {{flight.departureDate | formatDate}}
                  </div>
                  <div class="checkout-end__info">
                    <b>Departure time</b>
                    : {{flight.departureTime}}
                  </div>
                  <div class="checkout-end__info">
                    <b>Arrival airport</b>
                    : {{flight.arrivalAirport}}
                  </div>
                  <div class="checkout-end__info">
                    <b>Arrival date</b>
                    : {{flight.arrivalDate | formatDate}}
                  </div>
                  <div class="checkout-end__info">
                    <b>Arrival time</b>
                    : {{flight.arrivalTime}}
                  </div>
                </div>
              </template>

              <div v-if="booking.startingLocation" class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Starting location</h3>
                <div class="checkout-end__info" >
                  <b>Date</b>
                  : {{booking.startingLocation.date}}
                </div>
                 <div class="checkout-end__info" >
                  <b>Location</b>
                  : {{booking.startingLocation.location}}
                </div>
              </div>

              <div v-if="booking.finalLocation" class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Final location</h3>
                <div class="checkout-end__info" >
                  <b>Date</b>
                  : {{booking.finalLocation.date}}
                </div>
                <div class="checkout-end__info" >
                  <b>Location</b>
                  : {{booking.finalLocation.location}}
                </div>
              </div>


              <div class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Passenger Names</h3>
                <div
                  class="checkout-end__info"
                  v-for="(passenger, passengerIndex) in booking.passengers"
                  :key="passenger.passengerId"
                >
                  <b>{{passengerIndex + 1 | numberToWordsOrdinal | capitalize}}</b>
                  : {{passengerFullName(passenger)}}
                </div>
              </div>

              <div v-if="booking.excursions.length" class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Excursions</h3>
                <div
                  class="checkout-end__info"
                  v-for="excursion in booking.excursions"
                  :key="excursion.name"
                >
                  {{excursion.passengerCount}} &times; {{excursion.name}}
                  <br />
                  <b>Price</b>
                  : {{currency(excursion.price)}}
                </div>
              </div>

              <div v-if="booking.visas.length" class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Visas</h3>
                <div class="checkout-end__info" v-for="visa in booking.visas" :key="visa.id">
                  {{visa.passengerCount}} &times; {{visa.name}}
                  <br />
                  <b>Price</b>
                  : {{currency(visa.price)}}
                </div>
              </div>

              <div class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Payment and Balance</h3>
                <div class="checkout-end__info">
                  Total Price:
                  <b>{{currency(booking.total)}}</b>
                </div>
                <div class="checkout-end__info">
                  Amount paid:
                  <b>{{currency(booking.amountPaid)}}</b>
                </div>
                <div class="checkout-end__info">
                  Balance to pay:
                  <b>{{currency(booking.balanceToPay)}}</b>
                </div>
              </div>

              <div v-if="booking.donations.length" class="checkout-end__summary-section">
                <h3 class="checkout-end__summary-section-title">Thank you from Planeterra</h3>
                <div
                  class="checkout-end__info"
                  v-for="donation in booking.donations"
                  :key="donation.name"
                >
                  {{donation.passengerCount}} &times; {{donation.name}}
                  <br />
                  <b>Price</b>
                  : {{currency(donation.price)}}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import {Brand} from './../../../../brand.js';

export default {
  name: 'Booking',

  data() {
    return {
      Brand
    };
  },

  computed: {
    ...mapState('checkout', ['bookingReference', 'booking', 'data']),
    ...mapState(['checkout']),
    ...mapGetters('checkout', ['currency']),
    ...mapGetters(['passengerFullName']),

    confirmationType() {
      return this.data.booking.confirmations.find(
        confirmation => confirmation.type === this.booking.type
      );
    }
  },

  created() {
    // replace the url so the user cannot refresh the page and book the same tour again
    window.history.replaceState(
      null,
      null,
      `?bookingReference=${this.bookingReference}`
    );

    if(this.booking && this.booking.type !== "reserved" && this.booking.amountPaid > 0) {
      GTM.bookingPurchase(this.checkout);
    }

    if(this.booking && this.booking.type === "reserved") {
      GTM.bookingPurchase(this.checkout);
    }
  },

  mounted() {
    this.$nextTick().then(() => {
      picturefillBackground();
    });
  }
};
</script>
